import React, {useEffect} from 'react';
import type {SetRequired} from 'type-fest';
import {Checkbox, type CheckboxProps} from '@netx/core-components/Checkbox';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import useSharedRef from '@netx/core-components/hooks/useSharedRef';
import {splitEventHandler} from '@netx/core-next/utils';
import {useFormInput} from '../hooks';
import type {WithLegacyCommonFormFieldProps} from '../types';

export interface LegacyFormCheckboxProps
	extends WithLegacyCommonFormFieldProps<
		Omit<SetRequired<CheckboxProps, 'name'>, 'form'>
	> {
	value?: boolean;
}

export const LegacyFormCheckboxPrimitive = (
	{
		error,
		form,
		formFieldOptions,
		inputRef,
		onChange,
		...props
	}: LegacyFormCheckboxProps,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const innerInputRef = useSharedRef<HTMLInputElement | null>(null, [inputRef]);
	const _error = typeof error === 'string' ? error : error?.message;

	useFormInput(props.name, props.value, form, {
		required: props.required,
		validator: async (value) => {
			if (props.required && !value) {
				return new Error(i18n.t('i18n.required'));
			}
			return true;
		},
		...formFieldOptions,
	});

	useEffect(() => {
		if (!error) return;
		innerInputRef.current?.focus();
	}, [error]);

	const handleChange = useConstCallback(
		splitEventHandler(onChange, (event, value) => {
			form?.setValue(props.name, value);
		}),
	);

	return (
		<Checkbox
			ref={ref}
			{...props}
			checked={props.value}
			error={_error}
			inputRef={innerInputRef}
			onChange={handleChange}
		/>
	);
};

export const LegacyFormCheckbox = React.forwardRef(LegacyFormCheckboxPrimitive);

export default LegacyFormCheckbox;
